import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { getAuthToken } from './auth'
import { getPermissionsVersion, commitPermissionsVersion } from './permissionsVersion'
import { clearSession } from './session'
import SnackBar from '@component/SnackBar'

const ERROR_MESSAGE = {
  invalidUserToken: 'usr.aut.iut',
  notFoundUserToken: 'usr.aut.utm',
}

// Opt-in to the roles_and_permissions backend's new error contract: non-2xx
// status + JSON body { error: { code, message } }. Without this header the
// backend keeps the legacy develop behavior (HTTP 200 + Error header), which
// the success interceptor below still handles as a fallback.
export const API_ERROR_FORMAT_HEADER = 'x-api-error-format'
export const API_ERROR_FORMAT_VALUE = 'json'

// Dead-token codes that must tear down the session and bounce to /login.
// Legacy backend: usr.aut.iut / usr.aut.utm (HTTP 200 + Error header).
// New contract: auth.token.* at HTTP 401 (see backend ResponseHelper.sendError).
const SESSION_DEAD_CODES = new Set([
  ERROR_MESSAGE.invalidUserToken,
  ERROR_MESSAGE.notFoundUserToken,
  'auth.token.missing',
  'auth.token.invalid',
  'auth.token.expired',
])

// Reset.tsx renders its own inline error states for ResetPassword (used/expired
// link screen) — a generic toast on top of that is the double-error users reported.
// Login.tsx likewise renders its own inline error for AdminLogin failures.
const ignoreUrl = ['/csm/Group/GetGroups', '/csm/User/ResetPassword', '/csm/User/AdminLogin']

// The backend archives every active token on role/username/password changes
// (LogoutUserFromAllDevices), so a live tab can be holding a dead token. Direct
// axios calls bypass react-admin's checkError, so without an explicit redirect
// here the rejection strands the user on the route error boundary.
const redirectToLogin = () => {
  const { pathname } = window.location
  // pdfProofReport is rendered headlessly by puppeteer with a render token — never navigate it away.
  if (pathname.startsWith('/login') || pathname.includes('pdfProofReport')) return
  window.location.assign('/login')
}

function queryParams(params: any) {
  if (!params) return ''
  const segments: string[] = []
  Object.keys(params).forEach((k) => {
    const value = params[k]
    const noValue = !value && value !== 0
    if (noValue) {
      segments.push(`${k}=`)
      return
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        segments.push(`${k}=`)
        return
      }
      value.forEach((item) => {
        segments.push(`${k}=${item ?? ''}`)
      })
      return
    }
    segments.push(`${k}=${value}`)
  })
  return segments.join('&')
}

class axiosService {
  baseUrl: string = ''
  instance: AxiosInstance

  constructor() {
    const instance = axios.create()
    instance.interceptors.request.use(
      (config) => {
        // A caller-provided UserToken header wins (the reset-password page binds
        // each call to its tab's link token; the shared storage token must not
        // clobber it when several reset links/tabs are open at once).
        if (!config.headers.get('UserToken')) {
          const accessToken = getAuthToken()
          config.headers.set('UserToken', accessToken || '')
        }
        config.headers.set(API_ERROR_FORMAT_HEADER, API_ERROR_FORMAT_VALUE)
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    instance.interceptors.response.use(
      (response) => {
        // @ts-ignore
        response.duration = new Date().getTime()
        const pv = response.headers['permissions-version']
        if (pv && pv !== getPermissionsVersion()) {
          if (getPermissionsVersion() === null) {
            // First sighting this session — nothing to refresh against, just record it.
            commitPermissionsVersion(pv)
          } else {
            // Deliberately NOT committed here: usePermissionRefresh commits after a
            // successful CheckIn, so a transient refresh failure leaves the tracker
            // behind and the next response retriggers the refresh.
            window.dispatchEvent(new CustomEvent('permissions-version-changed', { detail: pv }))
          }
        }
        const errMsg = response.headers.error || response.headers.Error
        if (response.status === 200) {
          if (!errMsg) {
            return response.data.data
          }
          if (SESSION_DEAD_CODES.has(errMsg)) {
            // No queryClient passed: this path is followed by a full page navigation
            // (redirectToLogin), which discards the in-memory react-query cache anyway.
            clearSession()
            redirectToLogin()
            return Promise.reject(new Error(errMsg))
          }
          const responseUrl = response?.config?.url as string
          const errUrl = responseUrl.slice(responseUrl.indexOf('/csm'))
          if (!ignoreUrl.includes(errUrl)) {
            SnackBar({ msg: response.data })
          }
          return Promise.reject(response.data)
        }
      },
      (error) => {
        // New error contract (x-api-error-format: json): non-2xx status with
        // body { error: { code, message } }. Auth codes map to 401/403.
        const status = error?.response?.status
        const body = error?.response?.data
        const code = body?.error?.code
        if (code) {
          if (SESSION_DEAD_CODES.has(code)) {
            // Same dead-token teardown as the legacy 200+Error-header path above.
            clearSession()
            redirectToLogin()
          } else {
            const responseUrl = (error?.config?.url as string) || ''
            const errUrl = responseUrl.slice(responseUrl.indexOf('/csm'))
            if (!ignoreUrl.includes(errUrl)) {
              SnackBar({ msg: body.error.message || code })
            }
          }
          // Reject with the code as message plus status/body so authProvider
          // checkError can key off either.
          const err = new Error(code) as Error & { status?: number; body?: any }
          err.status = status
          err.body = body
          return Promise.reject(err)
        }
        return Promise.reject(error)
      }
    )
    this.instance = instance
  }

  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  get<T>(url: string, data?: any): Promise<T> {
    let curUrl = url
    if (data) {
      curUrl = url + '?' + queryParams(data)
    }
    return this.instance.get(`${this.baseUrl}${curUrl}`) as Promise<T>
  }

  post<T>(url: string, body: any, config?: AxiosRequestConfig) {
    return this.instance.post(`${this.baseUrl}${url}`, body, config) as Promise<T>
  }

  patch<T>(url: string, body: any) {
    return this.instance.patch(`${this.baseUrl}${url}`, body) as Promise<T>
  }

  delete(url: string) {
    return this.instance.delete(`${this.baseUrl}${url}`)
  }
}

export { resetPermissionsVersion } from './permissionsVersion'

export default new axiosService()
